import { useParams } from "@tanstack/react-router";
import { ZoomMtg } from "@zoom/meetingsdk";
import { useEffect, useRef, useState } from "react";
import { eventsJoinRoute } from "app/router";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";

/**
 * Zoom Meeting SDK launcher. Renders at /events/$id/join — a separate route
 * from the event detail page so the detail view can stay readable for
 * non-Zoom events. The detail page links here when the event has Zoom data.
 */
export const EventJoin = () => {
    const currentUser = useCurrentUser();
    const eventId = +useParams({ from: eventsJoinRoute.id }).id;
    const { data: event } = trpc.events.getSignatureForMeeting.useQuery({
        meetingNumber: eventId,
    });
    const [zoomLoaded, setZoomLoaded] = useState(false);
    const divRef = useRef(null);

    const signature = event?.signature;

    // Preload Zoom only when we have the necessary data
    useEffect(() => {
        if (signature && eventId && !zoomLoaded) {
            ZoomMtg.preLoadWasm();
            ZoomMtg.prepareWebSDK();
            setZoomLoaded(true);
        }
    }, [signature, eventId, zoomLoaded]);

    function startMeeting(signature: string) {
        if (!document.getElementById("zmmtg-root")) {
            console.error("Zoom SDK not properly loaded");
            return;
        }

        document.getElementById("zmmtg-root")!.style.display = "block";

        ZoomMtg.init({
            leaveUrl: `${window.location.origin}/social`,
            patchJsMedia: true,
            leaveOnPageUnload: true,
            success: () => {
                ZoomMtg.join({
                    signature: signature,
                    meetingNumber: eventId,
                    userName: "Compass",
                    userEmail: currentUser?.email || "info@compasscfosolutions.com",
                    sdkKey: "zR3S9ZWyRmi_p6TXNmXjMA",
                    passWord: "",
                    success: () => {
                        console.log("Zoom meeting joined successfully");
                    },
                    error: (error: unknown) => {
                        console.error("Zoom join error:", error);
                    },
                });
            },
            error: (error: unknown) => {
                console.error("Zoom init error:", error);
            },
        });
    }

    useEffect(() => {
        if (signature && eventId && zoomLoaded) {
            startMeeting(signature);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, eventId, zoomLoaded]);

    // Cleanup
    useEffect(() => {
        return () => {
            // Clear Zoom styles when exiting component
            const zoomStyles = document.getElementById("zoom-meetingsdk-css");
            if (zoomStyles) {
                zoomStyles.remove();
            }
        };
    }, []);

    return (
        <div className="flex flex-1 flex-col bg-slate-50 p-8">
            <div id="meetingSDKElement" ref={divRef}>
                {eventId}
            </div>
        </div>
    );
};
